import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import api, { apiErrorMessage } from '../api/client'
import {
  emptySimulationInput,
  toSimulationInput,
  type Project,
  type Simulation,
  type SimulationInput,
} from '../api/types'
import { computeResaleFeesDefault, computeResults } from '../compute'
import { useAmountsHidden } from '../privacy'
import PrivacyToggle from '../components/PrivacyToggle'
import ResultsPanel from '../components/ResultsPanel'
import { Mode } from '../components/resultsMode'
import AcquisitionCard from '../components/simulationForm/AcquisitionCard'
import FinancementCard from '../components/simulationForm/FinancementCard'
import type { FieldKey } from '../components/simulationForm/formFields'
import ProjectInfoCard from '../components/simulationForm/ProjectInfoCard'
import RentabiliteLocativeCard from '../components/simulationForm/RentabiliteLocativeCard'
import RentabiliteReventeCard from '../components/simulationForm/RentabiliteReventeCard'

export default function SimulationFormPage() {
  const { id } = useParams()
  const editing = Boolean(id)
  const navigate = useNavigate()
  useAmountsHidden()

  const [form, setForm] = useState<SimulationInput>({ ...emptySimulationInput })
  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [tab, setTab] = useState(0)
  const [linkedProject, setLinkedProject] = useState<{ id: number; status: string } | null>(null)

  // Loan principal and resale fees are suggested from other fields until the
  // user edits them directly, then they stop following the suggestion. Down
  // payment is always derived and not directly editable.
  const touchedLoanPrincipal = useRef(false)
  const touchedResaleFees = useRef(false)

  useEffect(() => {
    if (!editing) return
    api
      .get<Simulation>(`/simulations/${id}`)
      .then((res) => {
        setForm(toSimulationInput(res.data))
        setLinkedProject(res.data.project ?? null)
        touchedLoanPrincipal.current = true
        touchedResaleFees.current = true
      })
      .catch((err) => setError(apiErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [editing, id])

  useEffect(() => {
    if (touchedLoanPrincipal.current) return
    setForm((f) => ({ ...f, loanPrincipal: f.purchasePrice + f.renovationCosts }))
  }, [form.purchasePrice, form.renovationCosts])

  useEffect(() => {
    if (touchedResaleFees.current) return
    setForm((f) => ({ ...f, resaleFees: Math.round(computeResaleFeesDefault(f) * 100) / 100 }))
  }, [form.loanPrincipal, form.loanRate, form.loanDurationYears, form.holdingMonths])

  // Down payment is always derived, never edited directly: recompute during
  // render (not in an effect) and only touch state when the value actually
  // changes, so it stays in sync without an extra render round-trip.
  const derivedDownPayment = (form.purchasePrice * form.notaryFeeRate) / 100 + form.compromis
  if (form.downPayment !== derivedDownPayment) {
    setForm((f) => ({ ...f, downPayment: derivedDownPayment }))
  }

  const preview = useMemo(() => computeResults(form), [form])

  const setNum = (key: FieldKey, raw: string) => {
    if (key === 'loanPrincipal') touchedLoanPrincipal.current = true
    if (key === 'resaleFees') touchedResaleFees.current = true
    const value = raw === '' ? 0 : Number(raw)
    setForm((f) => ({ ...f, [key]: Number.isNaN(value) ? f[key] : value }))
  }

  const saveSimulation = async () => {
    const res = editing
      ? await api.put<Simulation>(`/simulations/${id}`, form)
      : await api.post<Simulation>('/simulations', form)
    return res.data
  }

  const saveDraft = async () => {
    setError('')
    setSaving(true)
    try {
      const sim = await saveSimulation()
      navigate(`/simulations/${sim.id}`)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const saveAsProject = async () => {
    setError('')
    setSaving(true)
    try {
      const sim = await saveSimulation()
      const project = await api.post<Project>('/projects', {
        name: sim.name,
        notes: '',
        simulationId: sim.id,
      })
      navigate(`/projects/${project.data.id}`)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <CircularProgress />

  const alreadyLinked = editing && Boolean(linkedProject)

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 3, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{editing ? 'Modifier la simulation' : 'Simulateur'}</Typography>
        <PrivacyToggle />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <ProjectInfoCard form={form} setForm={setForm} />

        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="Acquisition & Financement" sx={{ fontWeight: 'bold' }} />
          <Tab label="Rentabilité location" sx={{ fontWeight: 'bold' }} />
          <Tab label="Rentabilité revente" sx={{ fontWeight: 'bold' }} />
        </Tabs>

        {tab === 0 && (
          <>
            <AcquisitionCard form={form} preview={preview} setNum={setNum} />
            <FinancementCard form={form} setForm={setForm} preview={preview} setNum={setNum} />
          </>
        )}

        {tab === 1 && (
          <>
            <ResultsPanel r={preview} mode={Mode.Rental} hideToggle />
            <RentabiliteLocativeCard form={form} setNum={setNum} />
          </>
        )}

        {tab === 2 && (
          <>
            <ResultsPanel r={preview} mode={Mode.Resale} hideToggle />
            <RentabiliteReventeCard form={form} preview={preview} setNum={setNum} />
          </>
        )}

        <Stack direction="row" spacing={2}>
          {alreadyLinked ? (
            <>
              <Button variant="contained" size="large" disabled={saving} onClick={saveDraft}>
                Enregistrer
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<BusinessIcon />}
                component={RouterLink}
                to={`/projects/${linkedProject!.id}`}
              >
                Voir le projet
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" size="large" disabled={saving} onClick={saveDraft}>
                Enregistrer comme brouillon
              </Button>
              <Button variant="contained" size="large" disabled={saving} onClick={saveAsProject}>
                Créer un projet
              </Button>
            </>
          )}
          <Button variant="text" onClick={() => navigate(-1)}>
            Annuler
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
